import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import Request from '../../../../../helpers/request'
import * as url from '../../../../../helpers/url_helper'
import PaginationBar, { paginate, usePersistedPage } from '../../../../../components/Common/PaginationBar'
import { controlAddressForSwitch } from '../../../../../helpers/mesh_address'

interface Props {
    switches: any[]
    devices?: any[]
    relays: any[]
    pcbs?: any[]
    loading: boolean
    userId?: string
    placeId?: string
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const ActionBtn = ({ label, color, busy, onClick, title }: {
    label: string; color: string; busy: boolean; onClick: () => void; title?: string;
}) => (
    <button
        onClick={onClick}
        disabled={busy}
        title={title || label}
        style={{
            padding: '4px 10px', fontSize: 12, fontWeight: 500,
            background: '#fff', color, border: `1px solid ${color}`,
            borderRadius: 4, cursor: busy ? 'not-allowed' : 'pointer',
            marginRight: 4, opacity: busy ? 0.5 : 1,
        }}
    >
        {label}
    </button>
)

const renderOnline = (sw: any) => {
    if (sw.switchIsOnline === true) return <span style={{ color: '#16A34A' }}>● Online</span>
    if (sw.switchIsOnline === false) return <span style={{ color: '#9CA3AF' }}>○ Offline</span>
    return <span style={{ color: '#858D9D' }}>--</span>
}

// Plug-type switches (98 = Universal Plug, 99 = Indian Plug) are passive
// receptacles — they belong in Appliances, not Manual Controls.
const PLUG_TYPES = new Set([98, 99])
const isManualControl = (sw: any) => !PLUG_TYPES.has(Number(sw.type))

const ManualControlsView = ({ switches, devices = [], relays, pcbs = [], loading, userId, placeId }: Props) => {
    const navigate = useNavigate()
    const { roomId } = useParams()
    const [busySwId, setBusySwId] = useState<string | null>(null)
    const [appliancesTarget, setAppliancesTarget] = useState<any>(null)
    const [page, setPage] = usePersistedPage(`room-manualcontrols-${placeId}-${roomId}`)

    if (loading) return <p style={{ color: '#858D9D' }}>Loading…</p>

    const visibleSwitches = (switches || []).filter(isManualControl)
    const { paged, totalPages, safePage } = paginate(visibleSwitches, page)

    const relayMap: Record<string, string> = Object.fromEntries(
        (relays || []).map((r: any) => [r.id, r.name]),
    )
    const deviceById: Record<string, any> = Object.fromEntries(
        (devices || []).map((d: any) => [d.id, d]),
    )
    const pcbById: Record<string, any> = Object.fromEntries(
        (pcbs || []).map((p: any) => [p.id, p]),
    )

    const pingSwitch = (sw: any) => {
        // Ping the host PCB (same path the Modules tab uses) via PING_PCB.
        if (!sw.pcbId) {
            toast.error(`No host module found for ${sw.name || sw.id}`)
            return
        }
        setBusySwId(sw.id)
        Request.post(url.PING_PCB, { moduleId: sw.pcbId })
            .then(() => toast.success(`Ping sent to ${sw.name || sw.id}`))
            .catch((err: any) => toast.error(err?.response?.data?.msg || 'Ping failed'))
            .finally(() => setBusySwId(null))
    }

    // Default (no-group) association: switch.deviceId points to a single device.
    const appliancesForSwitch = (sw: any) => {
        if (!sw?.deviceId) return []
        const d = deviceById[sw.deviceId]
        return d ? [d] : []
    }

    return (
        <div>
            <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <TH>Name</TH>
                            <TH>Panel</TH>
                            <TH>Type</TH>
                            <TH>Online</TH>
                            <TH>Unicast</TH>
                            <TH>Actions</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {paged.length === 0 && (
                            <tr>
                                <td colSpan={6} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No manual controls in this room
                                </td>
                            </tr>
                        )}
                        {paged.map((sw, idx) => (
                            <tr key={sw.id || idx}>
                                <TD>{sw.name || sw.Name || '--'}</TD>
                                <TD>{relayMap[sw.relayId] || '--'}</TD>
                                <TD>{sw.type || '--'}</TD>
                                <TD>{renderOnline(sw)}</TD>
                                <TD>{(() => {
                                    const addr = controlAddressForSwitch(sw, pcbById[sw.pcbId])
                                    return addr != null ? addr : '--'
                                })()}</TD>
                                <TD>
                                    <ActionBtn
                                        label="Ping"
                                        color="#1a73e8"
                                        busy={busySwId === sw.id}
                                        onClick={() => pingSwitch(sw)}
                                        title="Ping this control"
                                    />
                                    <ActionBtn
                                        label="Appliances"
                                        color="#107569"
                                        busy={false}
                                        onClick={() => setAppliancesTarget(sw)}
                                        title="Show appliances linked to this control"
                                    />
                                    <ActionBtn
                                        label="Logs"
                                        color="#475467"
                                        busy={false}
                                        onClick={() => {
                                            if (userId && placeId) navigate(`/users/profile/${userId}/places/${placeId}/logs`)
                                        }}
                                        title="Open the Logs tab for this place"
                                    />
                                </TD>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <PaginationBar
                    page={safePage}
                    totalPages={totalPages}
                    total={visibleSwitches.length}
                    onPage={setPage}
                />
            </div>

            {appliancesTarget && (() => {
                const linked = appliancesForSwitch(appliancesTarget)
                return (
                    <div
                        data-testid="appliances-dialog"
                        style={{
                            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
                        }}
                        onClick={() => setAppliancesTarget(null)}
                    >
                        <div
                            onClick={e => e.stopPropagation()}
                            style={{
                                background: '#fff', borderRadius: 12, padding: 24,
                                maxWidth: 640, width: '90%', maxHeight: '80vh', overflow: 'auto',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
                            }}
                        >
                            <h4 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                                Appliances linked to <code style={{ fontSize: 13 }}>{appliancesTarget.name || appliancesTarget.id}</code>
                            </h4>
                            {linked.length > 0 ? (
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                                    <thead>
                                        <tr>
                                            <TH>Name</TH>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {linked.map((d: any) => (
                                            <tr key={d.id}>
                                                <TD>{d.name || '--'}</TD>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            ) : (
                                <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
                                    No appliances linked to this control
                                </div>
                            )}
                            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => setAppliancesTarget(null)}
                                    style={{ padding: '8px 16px', fontSize: 14, background: '#fff', color: '#374151', border: '1px solid #D1D5DB', borderRadius: 6, cursor: 'pointer' }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                )
            })()}
        </div>
    )
}

export default ManualControlsView
